import { EStreamAllDataDTO } from '../../types/estream/eStreamTrade';
import {
  InstrumentStoreItem,
  InstrumentStorePrice,
  InstrumentStorePriceType,
} from '../../types/estream/instrumentStoreItem';
import { InstrumentCategoryEnum, ListedInstrument } from '../../types/instrument/listedInstrument';
import { isDefined, isUsaPremarketTime } from '../../utils';
import { isDateIsTheLastTradingDay } from './eStreamMapperUtils';

export type EStreamMapperConsts = {
  betMarketCodes: Set<string>;
  usaMarketCodes: Set<string>;
  xetraFrMarketCodes: Set<string>;
};

export type EStreamPriceMapperStateContext = {
  market: string;
  data: EStreamAllDataDTO;
  previousValue: InstrumentStoreItem;
  hasRealtimeAccess: boolean;
  isPolled: boolean;
  listedInstrument: ListedInstrument | undefined;
  formatDate: (date: Date) => string;
  nonTradingDays: Record<string, Array<string>>;
  consts: EStreamMapperConsts;
};

export type RealTimePriceType =
  | InstrumentStorePriceType.RealTimeUp
  | InstrumentStorePriceType.RealTimeDown
  | InstrumentStorePriceType.RealTimeNeutral;

export type EStreamPriceMapperStateMachine = {
  next(context: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined;
};

export const StateMachine_Market1: EStreamPriceMapperStateMachine = {
  next({
    market,
    listedInstrument,
    data,
    consts,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    const { betMarketCodes, usaMarketCodes, xetraFrMarketCodes } = consts;
    if (listedInstrument?.instrumentCategory === InstrumentCategoryEnum.Currency) {
      return StateMachineGetPriceBasedOn_PRIMACT_1;
    } else if (betMarketCodes.has(market)) {
      if (listedInstrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
        return StateMachine_BuxFut_NormalTrading;
      }
      return StateMachine_PRC_QL_CD;
    } else if (usaMarketCodes.has(market)) {
      return StateMachine_TRD_STATUS_IS_H;
    } else if (xetraFrMarketCodes.has(market)) {
      return StateMachine_TRD_STATUS_IS_S;
    } else {
      console.warn(
        `Cannot map price because subscription is not registered using an instrument. Instrument: ${listedInstrument?.ric}(mic: "${listedInstrument?.mic}") EStream data: ${JSON.stringify(
          data,
        )}`,
      );
    }
    // it was undefined in the past
    return StateMachine_OFF_CLOSE2_IS_EMPTY;
  },
};

const StateMachineGetPriceBasedOn_PRIMACT_1: EStreamPriceMapperStateMachine = {
  next({
    data,
    listedInstrument,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.PRIMACT_1) {
      if (listedInstrument?.ric?.match(/JPY...=/)) {
        // JPY rate is multiplied by 100
        return {
          type: InstrumentStorePriceType.LastKnownPrice,
          value: data.PRIMACT_1 / 100,
        };
      }

      return {
        type: InstrumentStorePriceType.LastKnownPrice,
        value: data.PRIMACT_1,
      };
    }
    // it was undefined in the past
    return StateMachine_OFF_CLOSE2_IS_EMPTY;
  },
};

const StateMachine_TRD_STATUS_IS_H: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.TRD_STATUS === 'H') {
      return StateMachineGetPriceBasedOn_TRDPRC_1_IS_EMPTY;
    } else {
      return StateMachine_TRDPRC_1_IS_EMPTY;
    }
  },
};

const StateMachine_TRD_STATUS_IS_S: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.TRD_STATUS === 'S') {
      return StateMachineGetPriceBasedOn_TRDPRC_1_IS_EMPTY;
    } else {
      return StateMachine_TRDPRC_1_IS_EMPTY;
    }
  },
};

const StateMachineGetPriceBasedOn_TRDPRC_1_IS_EMPTY: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.TRDPRC_1) {
      return {
        type: InstrumentStorePriceType.LastKnownPrice,
        value: data.TRDPRC_1,
      };
    } else if (data.HST_CLOSE) {
      return {
        type: InstrumentStorePriceType.ClosePrice,
        value: data.HST_CLOSE,
      };
    } else {
      return StateMachineGetPriceBasedOnListedInstrument;
    }
  },
};

const StateMachineGetPriceBasedOnListedInstrument: EStreamPriceMapperStateMachine = {
  next({
    previousValue,
    listedInstrument,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (isDefined(previousValue.price)) {
      // if the previous value has a price
      return previousValue.price;
    } else if (isDefined(listedInstrument?.lastClosePrice)) {
      return {
        type: InstrumentStorePriceType.FromInstrumentService,
        value: listedInstrument.lastClosePrice,
      };
    }

    return undefined;
  },
};

const StateMachine_TRDPRC_1_IS_EMPTY: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.TRDPRC_1) {
      return StateMachine_OFF_CLOSE_IS_EMPTY;
    } else {
      return StateMachine_MARKET2;
    }
  },
};

const StateMachine_OFF_CLOSE_IS_EMPTY: EStreamPriceMapperStateMachine = {
  next({
    data,
    previousValue,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.OFF_CLOSE || (previousValue.raw?.OFF_CLOSE && data.OFF_CLOSE !== null)) {
      return StateMachine_MARKET2;
    } else {
      return StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_NOT_PREMARKET;
    }
  },
};

const StateMachine_OFF_CLOSE2_IS_EMPTY: EStreamPriceMapperStateMachine = {
  next({
    data,
    previousValue,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.OFF_CLOSE) {
      return {
        type: InstrumentStorePriceType.ClosePrice,
        value: data.OFF_CLOSE,
      };
    } else if (previousValue.raw?.OFF_CLOSE && data.OFF_CLOSE !== null) {
      return {
        type: InstrumentStorePriceType.ClosePrice,
        value: previousValue.raw?.OFF_CLOSE,
      };
    } else {
      return StateMachineGetPriceBasedOn_HSTCLS_DATE_LAST_TRADING_DAY;
    }
  },
};

const StateMachine_MARKET2: EStreamPriceMapperStateMachine = {
  next({
    market,
    consts,
  }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (consts.usaMarketCodes.has(market)) {
      return StateMachine_LOCALTIME_IS_BEFORE_MARKET_OPEN;
    } else {
      return StateMachine_OFF_CLOSE2_IS_EMPTY;
    }
  },
};

const StateMachine_LOCALTIME_IS_BEFORE_MARKET_OPEN: EStreamPriceMapperStateMachine = {
  next(_context: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (isUsaPremarketTime()) {
      return StateMachine_PREMKT_PRC_IS_EMPTY;
    } else {
      return StateMachine_OFF_CLOSE2_IS_EMPTY;
    }
  },
};

const StateMachine_PREMKT_PRC_IS_EMPTY: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (!data.PREMKT_PRC) {
      return StateMachine_OFF_CLOSE2_IS_EMPTY;
    } else {
      return StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_PREMARKET;
    }
  },
};

const isRealtimePriceType = (type: InstrumentStorePriceType | undefined): type is RealTimePriceType =>
  type === InstrumentStorePriceType.RealTimeUp ||
  type === InstrumentStorePriceType.RealTimeDown ||
  type === InstrumentStorePriceType.RealTimeNeutral;

const getRealtimeType = (
  { data, previousValue, listedInstrument }: EStreamPriceMapperStateContext,
  newPrice: number,
): RealTimePriceType => {
  const previousPrice = previousValue.price?.value || listedInstrument?.lastClosePrice;
  // PRCTCK_1 is more reliable than previousPrice
  // if (previousPrice && previousPrice === newPrice) {
  //   return InstrumentStorePriceType.RealTimeNeutral;
  // } else
  if (data.PRCTCK_1 === '⇧') {
    return InstrumentStorePriceType.RealTimeUp;
  } else if (data.PRCTCK_1 === '⇩') {
    return InstrumentStorePriceType.RealTimeDown;
  } else if (previousPrice) {
    if (previousPrice === newPrice) {
      // A previous real-time direction is still the best answer, keep it.
      if (isRealtimePriceType(previousValue.price?.type)) {
        return previousValue.price.type;
      }
      // Otherwise this is the first real-time update after a non real-time price (polled, delayed, close, ...).
      // Those types carry no direction, so use the daily change of the already collected details instead of
      // claiming the price is flat.
      const dailyChange = previousValue.details?.changeNominal;
      if (dailyChange) {
        return dailyChange > 0 ? InstrumentStorePriceType.RealTimeUp : InstrumentStorePriceType.RealTimeDown;
      }
      return InstrumentStorePriceType.RealTimeNeutral;
    }

    return previousPrice < newPrice ? InstrumentStorePriceType.RealTimeUp : InstrumentStorePriceType.RealTimeDown;
  } else {
    console.warn(`Can't decide realtime price type. Data: ${JSON.stringify(data)}`);
    return InstrumentStorePriceType.RealTimeNeutral;
  }
};

const StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_PREMARKET: EStreamPriceMapperStateMachine = {
  next(context: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    const { data, hasRealtimeAccess, isPolled } = context;
    if (!data.PREMKT_PRC) {
      // it was undefined in the past
      return StateMachine_OFF_CLOSE2_IS_EMPTY;
    }
    const value = data.PREMKT_PRC;
    const type = hasRealtimeAccess
      ? isPolled
        ? InstrumentStorePriceType.PolledPrice
        : getRealtimeType(context, value)
      : InstrumentStorePriceType.Delayed;
    return {
      type,
      value,
    };
  },
};

const StateMachineGetPriceBasedOn_HSTCLS_DATE_LAST_TRADING_DAY: EStreamPriceMapperStateMachine = {
  next(context: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (!context.data.HST_CLOSE) {
      return StateMachineGetPriceBasedOnListedInstrument;
    }

    const type = isDateIsTheLastTradingDay({
      market: context.market,
      dateStr: context.data.HSTCLSDATE,
      formatDate: context.formatDate,
      nonTradingDays: context.nonTradingDays,
    })
      ? InstrumentStorePriceType.ClosePrice
      : InstrumentStorePriceType.LastKnownPrice;

    return {
      type,
      value: context.data.HST_CLOSE,
    };
  },
};

const StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_NOT_PREMARKET: EStreamPriceMapperStateMachine = {
  next(context: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    const { hasRealtimeAccess, isPolled, data } = context;
    if (hasRealtimeAccess && data.TRDPRC_1) {
      const value = data.TRDPRC_1;
      const type = isPolled ? InstrumentStorePriceType.PolledPrice : getRealtimeType(context, value);

      return {
        type,
        value,
      };
    } else if (!hasRealtimeAccess && data.TRDPRC_1) {
      return {
        type: InstrumentStorePriceType.Delayed,
        value: data.TRDPRC_1,
      };
    } else {
      return StateMachine_OFF_CLOSE2_IS_EMPTY;
    }
  },
};

const PRC_QL_CD_REALTIME = ['', 'TRD', 'POS'];

const StateMachine_BuxFut_MarketClosed: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    // This can come back as "TH " - suspicios that it's always padded to 3 characters
    if (isDefined(data.PRC_QL_CD) && data.PRC_QL_CD.trim() === 'TH') {
      if (data.TRDPRC_1) {
        return {
          type: InstrumentStorePriceType.ClosePrice,
          value: data.TRDPRC_1,
        };
      }
    }
    return StateMachine_OFF_CLOSE2_IS_EMPTY;
  },
};

const StateMachine_BuxFut_Auction: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if (data.PRC_QL_CD === 'AUC' && data.THEO_VALUE) {
      return {
        type: InstrumentStorePriceType.IndicativePrice,
        value: data.THEO_VALUE,
      };
    }
    return StateMachine_BuxFut_MarketClosed;
  },
};

const StateMachine_BuxFut_NormalTrading: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    const isNormalTradingPhase = !data.PRC_QL_CD || PRC_QL_CD_REALTIME.includes(data.PRC_QL_CD.trim());
    if (isNormalTradingPhase && data.TRDPRC_1) {
      return StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_NOT_PREMARKET;
    }
    return StateMachine_BuxFut_Auction;
  },
};

const StateMachine_PRC_QL_CD: EStreamPriceMapperStateMachine = {
  next({ data }: EStreamPriceMapperStateContext): EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined {
    if ((data.PRC_QL_CD && PRC_QL_CD_REALTIME.includes(data.PRC_QL_CD.trim())) || data.TRADE_ID) {
      return StateMachineGetPriceBasedOn_REALTIME_SUBSCRIPTION_NOT_PREMARKET;
    } else if (data.PRC_QL_CD === 'AUC') {
      if (data.CLS_AUC) {
        return {
          type: InstrumentStorePriceType.IndicativePrice,
          value: data.CLS_AUC,
        };
      } else if (data.THEO_PRC) {
        return {
          type: InstrumentStorePriceType.IndicativePrice,
          value: data.THEO_PRC,
        };
      } else {
        // it was undefined in the past
        return StateMachine_OFF_CLOSE2_IS_EMPTY;
      }
    } else if (data.PRC_QL_CD !== 'AUC') {
      return StateMachine_MARKET2;
    }
    // it was undefined in the past
    return StateMachine_OFF_CLOSE2_IS_EMPTY;
  },
};
